import * as React from "react"
import { Box, Typography, Chip, Alert, CircularProgress, FormControlLabel, Checkbox } from "@mui/material"
import { ButtonSeeker, ModalSeeker } from "@ui-seeker/core"
import { AutocompleteAsyncDebounced } from "@components"

import { BACKEND_APIS } from "@constants"
import { useMerge } from "../@hooks"
import { tableData, selectedBrandIds } from "../@signals"


export default function BrandMergeModal() {
    const {
        isModalOpen,
        isMerging,
        targetBrandId,
        dryRun,
        handleCloseMergeModal,
        handleTargetBrandChange,
        handleDryRunChange,
        handleMerge,
        getSelectedBrandNames
    } = useMerge()

    const selectedBrandNames = getSelectedBrandNames()

    // Get currently selected brand IDs to exclude from autocomplete
    const excludedBrandIds = selectedBrandIds.value

    return (
        <ModalSeeker
            isOpen={isModalOpen}
            title="Merge Brands"
            controller={handleCloseMergeModal}
            hideActions
            containerProps={{ maxWidth: "sm" }}
        >
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 3 }}>
                <Alert severity="warning">
                    This action will merge the selected brands into the target brand.
                    All SKU mappings and SKUs will be updated to reference the target brand.
                    The merged brands will be marked as deleted.
                </Alert>

                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        Brands to merge ({selectedBrandNames.length}):
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {selectedBrandNames.map((name, index) => (
                            <Chip
                                key={index}
                                label={name}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        ))}
                    </Box>
                </Box>

                <Box>
                    <Typography variant="subtitle2" gutterBottom>
                        Target brand (merge into):
                    </Typography>
                    <AutocompleteAsyncDebounced
                        urlAjax={`${BACKEND_APIS.BRANDS}/crud/brand/find-options`}
                        label="Select target brand"
                        placeholder="Search for a brand..."
                        value={targetBrandId}
                        onChange={handleTargetBrandChange}
                        filterOptions={(options) =>
                            options.filter(opt => !excludedBrandIds.includes(opt.value))
                        }
                    />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={dryRun}
                                onChange={handleDryRunChange}
                                color="warning"
                            />
                        }
                        label="Dry Run (test without changes)"
                    />
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <ButtonSeeker
                            variant="outlined"
                            onClick={handleCloseMergeModal}
                            disabled={isMerging}
                        >
                            Cancel
                        </ButtonSeeker>
                        <ButtonSeeker
                            variant="contained"
                            color={dryRun ? "warning" : "primary"}
                            onClick={handleMerge}
                            disabled={!targetBrandId || isMerging}
                            startIcon={isMerging ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {isMerging ? "Processing..." : dryRun ? "Test Merge" : "Merge Brands"}
                        </ButtonSeeker>
                    </Box>
                </Box>
            </Box>
        </ModalSeeker>
    )
}
